import { useAuth, useLocalStorage } from "@elit-hair/elit-tools";
import { createContext, useContext, useEffect, useMemo, useRef } from "react";

import { LOCAL_STORAGE_KEYS } from "@/constants/local-storage-keys";
import { useAssignedClinics } from "@/queries/clinic.queries";
import { cleanupUserSpecificStorageKeys } from "@/utils/local-storage.utils";

type ClinicContextType = {
    selectedClinic: string | null;
    setSelectedClinic: (clinicId: string | null) => void;
};

const ClinicContext = createContext<ClinicContextType | undefined>(undefined);

export const ClinicProvider = ({ children }: { children: React.ReactNode }) => {
    const { userData } = useAuth();
    const userId = userData?.id || null;
    const { data: availableClinics = [], isLoading } = useAssignedClinics();
    const hasInitialized = useRef(false);
    const lastSelectedClinic = useRef<string | null>(null);
    const lastUserId = useRef<string | null>(null); // Track last user id to detect real changes

    const storageKey = useMemo(
        () => (userId ? `${LOCAL_STORAGE_KEYS.SELECTED_CLINIC.key}_${userId}` : LOCAL_STORAGE_KEYS.SELECTED_CLINIC.key),
        [userId]
    );

    const [selectedClinic, setSelectedClinicState] = useLocalStorage<string | null>(
        storageKey,
        LOCAL_STORAGE_KEYS.SELECTED_CLINIC.default
    );

    // Effect: when the user changes, reset internal flags and re-read from localStorage
    useEffect(() => {
        if (userId !== lastUserId.current) {
            hasInitialized.current = false;
            lastUserId.current = userId;

            if (!userId) {
                // No authenticated user: clear any previous selection
                setSelectedClinicState(null);
                lastSelectedClinic.current = null;
                return;
            }

            const storedValue = localStorage.getItem(storageKey);

            if (!storedValue) {
                // New user with no stored clinic: clear selection so auto-selection logic can run
                setSelectedClinicState(null);
                lastSelectedClinic.current = null;
                return;
            }

            try {
                const parsed = JSON.parse(storedValue);
                setSelectedClinicState(parsed);
                lastSelectedClinic.current = parsed;
            } catch {
                // If parsing fails, let the main effect handle the logic
                setSelectedClinicState(null);
                lastSelectedClinic.current = null;
            }
        }
    }, [userId, storageKey, setSelectedClinicState]);

    useEffect(() => {
        if (!userId) return;
        cleanupUserSpecificStorageKeys(storageKey, LOCAL_STORAGE_KEYS.SELECTED_CLINIC.key);
    }, [userId, storageKey]);

    useEffect(() => {
        if (isLoading) return;

        if (!hasInitialized.current) {
            hasInitialized.current = true;
            lastSelectedClinic.current = selectedClinic;

            // Initial auto-selection:
            // - If user has at least one available clinic and none selected yet (nothing in storage), auto-select the first
            // - Otherwise keep the current behavior based on storage
            if (availableClinics.length > 0 && !selectedClinic) {
                const firstClinicId = availableClinics[0].id;
                setSelectedClinicState(firstClinicId);
                lastSelectedClinic.current = firstClinicId;
            }
            return;
        }

        if (!selectedClinic) return;
        if (selectedClinic === lastSelectedClinic.current) return;

        lastSelectedClinic.current = selectedClinic;

        const isSelectedClinicAvailable = availableClinics.some((c) => c.id === selectedClinic);
        if (isSelectedClinicAvailable) return;

        // If the previously selected clinic is no longer available:
        // - If there is exactly one clinic, auto-select it
        // - Otherwise (zero or multiple), clear selection and let user choose
        if (availableClinics.length === 1) {
            setSelectedClinicState(availableClinics[0].id);
            lastSelectedClinic.current = availableClinics[0].id;
            return;
        }

        setSelectedClinicState(null);
        lastSelectedClinic.current = null;
    }, [availableClinics, selectedClinic, isLoading, setSelectedClinicState]);

    const setSelectedClinic = (clinicId: string | null) => {
        setSelectedClinicState(clinicId);
        lastSelectedClinic.current = clinicId;
    };

    return <ClinicContext.Provider value={{ selectedClinic, setSelectedClinic }}>{children}</ClinicContext.Provider>;
};

export const useClinic = () => {
    const context = useContext(ClinicContext);
    if (!context) {
        throw new Error("useClinic must be used within a ClinicProvider");
    }
    return context;
};
